import clsx from "clsx";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import classes from "./styles.module.scss";

import { Typography } from "@components/atoms/typography";
import { VNotifVariants } from "@components/atoms/notify/variants";
import type {
  INotifMessage,
  INotifMessageProps,
  INotifProps,
  INotifType,
} from "@components/atoms/notify/types";

const getMessageTitle = (
  title: string | undefined,
  type: INotifType,
  t: TFunction<"form">,
) => {
  if (title) return title;
  switch (type) {
    case "success":
      return t("success");
    case "danger":
      return t("errors.default");
    case "warning":
      return t("warning");
    case "info":
      return t("info");
    case "promise":
      return "";
    default:
      return "";
  }
};

function extractMsg(
  msg: INotifMessage | string,
  type: INotifType,
  t: TFunction<"form">,
) {
  if (typeof msg === "object") {
    return {
      title: getMessageTitle(msg.title, type, t),
      text: msg.text,
    };
  }
  if (type === "danger") {
    return { title: t("errors.default"), text: msg };
  }
  if (type === "success") {
    return { title: t("success"), text: msg };
  }
  if (type === "warning") {
    return { title: t("warning"), text: msg };
  }
  if (type === "info") {
    return { title: t("info"), text: msg };
  }
  return { title: "", text: msg };
}

/**
 * Displays a notification with a specified type and message.
 * @param {INotifProps} props - Notification properties.
 * @returns Notif Notification component.
 */
export function Notif(props: INotifProps) {
  const { t } = useTranslation("form");

  /**
   * The message content of the notification.
   */
  const { msg } = props;

  /**
   * The type of the notification (e.g., 'info', 'warning', 'danger', 'success', 'promise').
   */
  const { type } = props;

  /**
   * The title of the notification
   */
  const { title, text } = extractMsg(msg, type, t);

  /**
   * The icon and color for the notification based on its type.
   */
  const { icon, color } = VNotifVariants[type];

  return (
    <div className={clsx(classes.notif)} data-variant={type}>
      <div className={clsx(classes.notif__contents)}>
        <div className={clsx(classes.notif__head)}>
          <div className={clsx(classes.notif__icon)}>{icon}</div>
          <div>
            <Typography fontFamily={"Morabba"} variant={"h5"} color={color}>
              {title}
            </Typography>
          </div>
        </div>
      </div>
      <Typography
        fontFamily={"dana"}
        fontWeight={"regular"}
        fontSize={"sm"}
        className={clsx(classes.text)}
      >
        {text}
      </Typography>
    </div>
  );
}

/**
 * Displays an information notification with the specified message.
 * @param {INotifMessageProps} props - Information notification properties.
 * @returns InfoNotify Information notification component.
 */
export function InfoNotify(props: INotifMessageProps) {
  /**
   * The message content of the information notification.
   */
  const { msg } = props;

  return <Notif msg={msg} type={"info"} />;
}

/**
 * Displays a warning notification with the specified message.
 * @param {INotifMessageProps} props - Warning notification properties.
 * @returns WarningNotify Warning notification component.
 */
export function WarningNotify(props: INotifMessageProps) {
  /**
   * The message content of the warning notification.
   */
  const { msg } = props;

  return <Notif msg={msg} type={"warning"} />;
}

/**
 * Displays an error notification with the specified message.
 * @param {INotifMessageProps} props - Error notification properties.
 * @returns ErrorNotify Error notification component.
 */
export function ErrorNotify(props: INotifMessageProps) {
  /**
   * The message content of the error notification.
   */
  const { msg } = props;

  return <Notif msg={msg} type={"danger"} />;
}

/**
 * Displays a promise notification with the specified message.
 * @param {INotifMessageProps} props - Promise notification properties.
 * @returns PromiseNotify Promise notification component.
 */
export function PromiseNotify(props: INotifMessageProps) {
  /**
   * The message content of the promise notification.
   */
  const { msg } = props;

  return <Notif msg={msg} type={"promise"} />;
}

/**
 * Displays a success notification with the specified message.
 * @param {INotifMessageProps} props - Success notification properties.
 * @returns SuccessNotify Success notification component.
 */
export function SuccessNotify(props: INotifMessageProps) {
  /**
   * The message content of the success notification.
   */
  const { msg } = props;

  return <Notif msg={msg} type={"success"} />;
}
